import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';
import { useChartPreferences } from '../context/ChartPreferencesContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Save, ChevronLeft, ChevronRight, TrendingUp, Package, Plus, Trash2, 
  ChevronDown, DollarSign, Edit2, FileSpreadsheet, BarChart3, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatPercent, formatNumber, cn } from '../lib/utils';
import { exportPricingToExcel } from './ExportExcelButton';
import { UnsavedChangesDialog, useUnsavedBlocker, BlockerDialog } from './UnsavedChangesDialog';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList,
  LineChart, Line, ComposedChart, Area
} from 'recharts';

const COLORS = ['#3B82F6', '#0EA5E9', '#10B981', '#F59E0B', '#8B5CF6'];

export const PricingPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { currentProject, fetchProject, updateProject, getCalculation } = useProject();
  const { showLabels, formatChartValue, formatTableValue, formatVolumeValue } = useChartPreferences();
  
  const [products, setProducts] = useState([]);
  const [salesProjection, setSalesProjection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('inputs');
  const [editingProduct, setEditingProduct] = useState(null);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [initialProducts, setInitialProducts] = useState(null);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [selectedProductFilter, setSelectedProductFilter] = useState('all');

  useEffect(() => {
    const loadData = async () => {
      if (!currentProject || currentProject.id !== projectId) {
        await fetchProject(projectId);
      }
    };
    loadData();
  }, [projectId, currentProject, fetchProject]);

  useEffect(() => {
    if (currentProject?.products) {
      const initializedProducts = currentProject.products.map(p => ({
        ...p,
        unit_price: p.unit_price || 0,
        costs: (p.costs && p.costs.length > 0) 
          ? p.costs.map(c => ({ ...c, growth_type: c.growth_type || 'inflation', manual_amounts: c.manual_amounts || {} }))
          : [{ id: crypto.randomUUID(), name: 'Costo Principal', amount: p.unit_cost || 0, growth_type: 'inflation', manual_amounts: {} }],
        price_growth_type: p.price_growth_type || 'inflation',
        cost_growth_type: p.cost_growth_type || 'inflation',
        manual_prices: p.manual_prices || {}
      }));
      setProducts(initializedProducts);
      setInitialProducts(JSON.stringify(initializedProducts));
    }
  }, [currentProject]);

  useEffect(() => {
    const loadProjection = async () => {
      if (activeTab === 'summary' && products.some(p => p.unit_price > 0)) {
        try {
          const data = await getCalculation(projectId, 'sales');
          setSalesProjection(data);
        } catch (err) {
          console.error('Error loading sales projection:', err);
        }
      }
    };
    loadProjection();
  }, [activeTab, products, projectId, getCalculation]);

  const years = currentProject?.specifications?.project_life_years || 5;
  const inflation = currentProject?.financing?.inflation_rate || 4;
  const currency = currentProject?.specifications?.currency || 'MXN';
  const daysPerYear = currentProject?.specifications?.operates_seven_days ? 365 : 312;
  const daysPerMonth = daysPerYear / 12;
  const pricingOperatingMonths = currentProject?.operating_months || Array(years).fill(12);

  const getTotalCost = (product) => {
    if (!product.costs || product.costs.length === 0) return product.unit_cost || 0;
    return product.costs.reduce((sum, c) => sum + (c.amount || 0), 0);
  };

  const openProductDialog = (product) => {
    setEditingProduct({ ...product, manual_prices: { ...product.manual_prices } });
    setShowProductDialog(true);
  };

  const saveProductFromDialog = () => {
    if (!editingProduct) return;
    const totalCost = editingProduct.costs?.reduce((sum, c) => sum + (c.amount || 0), 0) || 0;
    setProducts(prev => prev.map(p => 
      p.id === editingProduct.id ? { ...editingProduct, unit_cost: totalCost } : p
    ));
    setShowProductDialog(false);
    setEditingProduct(null);
    toast.success('Producto actualizado');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const productsToSave = products.map(p => ({ ...p, unit_cost: getTotalCost(p) }));
      await updateProject(projectId, { products: productsToSave });
      setInitialProducts(JSON.stringify(products));
      toast.success('Precios y costos guardados');
    } catch (err) {
      if (err.response?.status !== 403) toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const hasUnsavedChanges = initialProducts && JSON.stringify(products) !== initialProducts;

  const { showDialog, tryNavigate, handleSaveAndProceed, handleDiscard, handleCancel } = useUnsavedBlocker(hasUnsavedChanges, handleSave);

  // Frontend projection for a single product
  const calculateProductProjection = (product) => {
    const baseVolume = product.base_volume || 0;
    const basePrice = product.unit_price || 0;
    const priceGrowthType = product.price_growth_type || 'inflation';
    const costs = (product.costs && product.costs.length > 0) ? product.costs : [];
    const baseCostTotal = costs.reduce((sum, c) => sum + (c.amount || 0), 0) || product.unit_cost || 0;

    const result = [];
    for (let year = 1; year <= years; year++) {
      const opMonths = pricingOperatingMonths[year - 1] || 12;
      const annualBase = product.input_frequency === 'daily' 
        ? baseVolume * daysPerMonth * opMonths 
        : baseVolume * opMonths;
      
      let volume;
      if (product.growth_type === 'fixed') {
        volume = annualBase;
      } else if (product.growth_type === 'growth' || product.growth_type === 'percentage') {
        volume = annualBase * Math.pow(1 + (product.growth_percentage || 0) / 100, year - 1);
      } else if (product.growth_type === 'decline') {
        volume = annualBase * Math.pow(1 - (product.growth_percentage || 0) / 100, year - 1);
      } else { // manual
        const mv = product.manual_volumes?.[String(year)] ?? product.manual_volumes?.[year];
        if (mv != null) {
          volume = product.input_frequency === 'daily' ? mv * daysPerMonth * opMonths : mv * opMonths;
        } else {
          volume = annualBase;
        }
      }

      let price;
      if (priceGrowthType === 'inflation') {
        price = basePrice * Math.pow(1 + inflation / 100, year - 1);
      } else {
        const mp = product.manual_prices?.[String(year)];
        price = (mp?.price != null && mp.price > 0) ? mp.price : basePrice;
      }

      // Calculate cost: sum of each individual cost with its own growth
      let cost;
      if (costs.length > 0) {
        cost = 0;
        for (const ci of costs) {
          if (ci.growth_type === 'manual' && ci.manual_amounts) {
            const yrAmount = ci.manual_amounts[String(year)];
            cost += (yrAmount != null) ? yrAmount : (ci.amount || 0);
          } else {
            cost += (ci.amount || 0) * Math.pow(1 + inflation / 100, year - 1);
          }
        }
      } else {
        cost = baseCostTotal * Math.pow(1 + inflation / 100, year - 1);
      }

      const revenue = volume * price;
      const totalCost = volume * cost;
      const margin = revenue - totalCost;
      const marginPct = revenue > 0 ? (margin / revenue * 100) : 0;

      result.push({ year, volume: Math.round(volume), price, cost, revenue, totalCost, margin, marginPct });
    }
    return result;
  };

  const calcCAGR = (initial, final, n) => {
    if (!initial || initial <= 0 || n <= 0) return 0;
    return (Math.pow(final / initial, 1 / n) - 1) * 100;
  };

  // KPIs
  const totalProjectedRevenue = products.reduce((sum, p) => {
    const proj = calculateProductProjection(p);
    return sum + (proj[0]?.revenue || 0);
  }, 0);

  const avgMargin = (() => {
    const configured = products.filter(p => p.unit_price > 0);
    if (configured.length === 0) return 0;
    return configured.reduce((sum, p) => {
      const tc = getTotalCost(p);
      return sum + ((p.unit_price - tc) / p.unit_price * 100);
    }, 0) / configured.length;
  })();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="pricing-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <span className="px-2 py-1 bg-primary/20 text-primary rounded text-xs font-medium">PASO 5 DE 10</span>
          </div>
          <h1 className="text-4xl font-bold">Precio y Costos de Venta</h1>
          <p className="text-muted-foreground mt-1">
            Haz clic en cada producto para configurar su precio y desglosar sus costos.
          </p>
        </div>
        <Button 
          variant="outline" className="gap-2"
          onClick={() => exportPricingToExcel(products, currentProject?.name || 'Proyecto', currency)}
        >
          <FileSpreadsheet className="w-4 h-4" />
          Excel
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-950/50 to-background border-blue-500/30">
          <CardContent className="p-4">
            <p className="text-xs text-blue-400 mb-1">Productos Configurados</p>
            <p className="text-3xl font-bold">{products.filter(p => p.unit_price > 0).length} / {products.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-950/50 to-background border-green-500/30">
          <CardContent className="p-4">
            <p className="text-xs text-green-400 mb-1">Margen Promedio</p>
            <p className="text-3xl font-bold">{formatPercent(avgMargin)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-950/50 to-background border-amber-500/30">
          <CardContent className="p-4">
            <p className="text-xs text-amber-400 mb-1">Ingresos Proyectados (Año 1)</p>
            <p className="text-3xl font-bold">{formatCurrency(totalProjectedRevenue, currency)}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-secondary/50">
          <TabsTrigger value="inputs">PRODUCTOS</TabsTrigger>
          <TabsTrigger value="summary">
            <BarChart3 className="w-4 h-4 mr-2" />
            ANÁLISIS COMPLETO
          </TabsTrigger>
        </TabsList>

        {/* TAB: PRODUCTOS */}
        <TabsContent value="inputs" className="mt-6 space-y-3">
          {products.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="p-12 text-center">
                <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h4 className="text-lg font-semibold mb-2">No hay productos</h4>
                <p className="text-muted-foreground mb-4">Primero debes agregar productos en Volumen de Ventas</p>
                <Button onClick={() => navigate(`/app/project/${projectId}/sales-volume`)}>Ir a Volumen de Ventas</Button>
              </CardContent>
            </Card>
          ) : (
            products.map((product, idx) => {
              const totalCost = getTotalCost(product);
              const margin = product.unit_price > 0 ? ((product.unit_price - totalCost) / product.unit_price * 100) : 0;
              const isConfigured = product.unit_price > 0 && totalCost > 0;
              
              return (
                <Card 
                  key={product.id}
                  className={cn("cursor-pointer transition-all hover:shadow-lg", !isConfigured && "border-warning/50 bg-warning/5")}
                  onClick={() => openProductDialog(product)}
                  data-testid={`product-row-${idx}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl flex-shrink-0 flex items-center justify-center overflow-hidden"
                        style={{ backgroundColor: `${COLORS[idx % COLORS.length]}20` }}>
                        {product.photo ? (
                          <img src={product.photo} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Package className="w-6 h-6" style={{ color: COLORS[idx % COLORS.length] }} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-lg truncate">{product.name || 'Sin nombre'}</h3>
                        <p className="text-sm text-muted-foreground">
                          {product.price_growth_type === 'manual' ? 'Precio: Override Manual' : `Precio: +Inflación (${formatPercent(inflation)})`}
                          {' | '}
                          {product.cost_growth_type === 'manual' ? 'Costo: Override Manual' : `Costo: +Inflación (${formatPercent(inflation)})`}
                        </p>
                      </div>
                      <div className="text-center px-4 border-l border-border">
                        <p className="text-xs text-muted-foreground">Precio</p>
                        <p className="text-lg font-bold text-primary">{product.unit_price > 0 ? formatCurrency(product.unit_price, currency) : '--'}</p>
                      </div>
                      <div className="text-center px-4 border-l border-border">
                        <p className="text-xs text-muted-foreground">Costo</p>
                        <p className="text-lg font-bold text-destructive">{totalCost > 0 ? formatCurrency(totalCost, currency) : '--'}</p>
                      </div>
                      <div className="text-center px-4 border-l border-border">
                        <p className="text-xs text-muted-foreground">Margen</p>
                        <p className={cn("text-lg font-bold", margin > 30 ? "text-success" : margin > 15 ? "text-warning" : "text-destructive")}>
                          {isConfigured ? formatPercent(margin) : '--'}
                        </p>
                      </div>
                      <div className={cn("w-3 h-3 rounded-full", isConfigured ? "bg-success" : "bg-warning animate-pulse")} />
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>

        {/* TAB: ANÁLISIS COMPLETO */}
        <TabsContent value="summary" className="mt-6 space-y-6">
          {products.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="p-12 text-center text-muted-foreground">Agrega productos para ver el análisis</CardContent>
            </Card>
          ) : (
            <>
              {/* Filter */}
              <div className="flex justify-end">
                <Select value={selectedProductFilter} onValueChange={setSelectedProductFilter}>
                  <SelectTrigger className="w-56">
                    <SelectValue placeholder="Todos los productos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los productos</SelectItem>
                    {products.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Revenue & Cost Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Ingresos vs Costos Proyectados</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <ComposedChart data={(() => {
                      const data = [];
                      for (let y = 1; y <= years; y++) {
                        const row = { year: `Año ${y}`, ingresos: 0, costos: 0, margen: 0 };
                        products.filter(p => selectedProductFilter === 'all' || p.id === selectedProductFilter).forEach(p => {
                          const proj = calculateProductProjection(p);
                          const yr = proj[y - 1];
                          if (yr) {
                            row.ingresos += yr.revenue;
                            row.costos += yr.totalCost;
                            row.margen += yr.margin;
                          }
                        });
                        data.push(row);
                      }
                      return data;
                    })()} margin={{ top: 20, right: 10, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
                      <XAxis dataKey="year" stroke="#94A3B8" fontSize={12} />
                      <YAxis stroke="#94A3B8" fontSize={12} tickFormatter={(v) => formatChartValue(v)} domain={[0, (dataMax) => Math.ceil(dataMax * 1.15)]} />
                      <Tooltip contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142' }} formatter={(v) => formatCurrency(v, currency)} />
                      <Legend />
                      <Bar dataKey="ingresos" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Ingresos">
                        {showLabels && <LabelList dataKey="ingresos" position="top" formatter={(v) => formatChartValue(v)} fill="#94A3B8" fontSize={9} />}
                      </Bar>
                      <Bar dataKey="costos" fill="#EF4444" radius={[4, 4, 0, 0]} name="Costos">
                        {showLabels && <LabelList dataKey="costos" position="top" formatter={(v) => formatChartValue(v)} fill="#94A3B8" fontSize={9} />}
                      </Bar>
                      <Line type="monotone" dataKey="margen" stroke="#10B981" strokeWidth={2} name="Margen" dot={{ fill: '#10B981', r: 4 }}>
                        {showLabels && <LabelList dataKey="margen" position="top" formatter={(v) => formatChartValue(v)} fill="#10B981" fontSize={9} />}
                      </Line>
                    </ComposedChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Per-product annual tables */}
              {products
                .filter(p => selectedProductFilter === 'all' || p.id === selectedProductFilter)
                .map((product, pIdx) => {
                const proj = calculateProductProjection(product);
                const n = years - 1;
                const y1 = proj[0];
                const yL = proj[proj.length - 1];

                return (
                  <Card key={product.id} className="border-l-4" style={{ borderLeftColor: COLORS[pIdx % COLORS.length] }}>
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2">
                        <Package className="w-5 h-5" style={{ color: COLORS[pIdx % COLORS.length] }} />
                        {product.name} - Desglose Anual
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <Table className="text-xs">
                          <TableHeader>
                            <TableRow className="bg-secondary/30">
                              <TableHead className="font-bold">Concepto</TableHead>
                              {proj.map((_, i) => (
                                <TableHead key={i} className="text-right font-bold">Año {i + 1}</TableHead>
                              ))}
                              <TableHead className="text-right font-bold bg-primary/10">CAGR</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            <TableRow>
                              <TableCell className="font-medium">Volumen (unidades)</TableCell>
                              {proj.map((y, i) => (
                                <TableCell key={i} className="text-right tabular-nums">{formatVolumeValue(y.volume)}</TableCell>
                              ))}
                              <TableCell className="text-right font-bold tabular-nums bg-primary/5">
                                {calcCAGR(y1.volume, yL.volume, n) > 0 ? '+' : ''}{calcCAGR(y1.volume, yL.volume, n).toFixed(1)}%
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className="font-medium">Precio Unitario</TableCell>
                              {proj.map((y, i) => (
                                <TableCell key={i} className="text-right tabular-nums">{formatTableValue(y.price, currency)}</TableCell>
                              ))}
                              <TableCell className="text-right font-bold tabular-nums bg-primary/5">
                                {calcCAGR(y1.price, yL.price, n) > 0 ? '+' : ''}{calcCAGR(y1.price, yL.price, n).toFixed(1)}%
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className="font-medium">Costo Unitario</TableCell>
                              {proj.map((y, i) => (
                                <TableCell key={i} className="text-right tabular-nums text-destructive">{formatTableValue(y.cost, currency)}</TableCell>
                              ))}
                              <TableCell className="text-right font-bold tabular-nums bg-primary/5">
                                {calcCAGR(y1.cost, yL.cost, n) > 0 ? '+' : ''}{calcCAGR(y1.cost, yL.cost, n).toFixed(1)}%
                              </TableCell>
                            </TableRow>
                            <TableRow className="bg-blue-500/10">
                              <TableCell className="font-bold text-blue-400">Ingresos</TableCell>
                              {proj.map((y, i) => (
                                <TableCell key={i} className="text-right tabular-nums font-semibold text-blue-400">{formatTableValue(y.revenue, currency)}</TableCell>
                              ))}
                              <TableCell className="text-right font-bold tabular-nums bg-primary/10 text-blue-400">
                                {calcCAGR(y1.revenue, yL.revenue, n) > 0 ? '+' : ''}{calcCAGR(y1.revenue, yL.revenue, n).toFixed(1)}%
                              </TableCell>
                            </TableRow>
                            <TableRow className="bg-red-500/10">
                              <TableCell className="font-bold text-red-400">Costos Totales</TableCell>
                              {proj.map((y, i) => (
                                <TableCell key={i} className="text-right tabular-nums font-semibold text-red-400">{formatTableValue(y.totalCost, currency)}</TableCell>
                              ))}
                              <TableCell className="text-right font-bold tabular-nums bg-primary/10 text-red-400">
                                {calcCAGR(y1.totalCost, yL.totalCost, n) > 0 ? '+' : ''}{calcCAGR(y1.totalCost, yL.totalCost, n).toFixed(1)}%
                              </TableCell>
                            </TableRow>
                            <TableRow className="bg-emerald-500/10 border-t-2 border-border">
                              <TableCell className="font-bold text-emerald-400">Utilidad Bruta</TableCell>
                              {proj.map((y, i) => (
                                <TableCell key={i} className="text-right tabular-nums font-bold text-emerald-400">{formatTableValue(y.margin, currency)}</TableCell>
                              ))}
                              <TableCell className="text-right font-bold tabular-nums bg-primary/10 text-emerald-400">
                                {calcCAGR(y1.margin, yL.margin, n) > 0 ? '+' : ''}{calcCAGR(y1.margin, yL.margin, n).toFixed(1)}%
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className="font-medium">% Margen</TableCell>
                              {proj.map((y, i) => (
                                <TableCell key={i} className={cn("text-right tabular-nums font-semibold", y.marginPct > 30 ? "text-success" : y.marginPct > 15 ? "text-warning" : "text-destructive")}>
                                  {y.marginPct.toFixed(1)}%
                                </TableCell>
                              ))}
                              <TableCell className="text-right bg-primary/5">--</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              {/* Ticket Promedio Global */}
              {selectedProductFilter === 'all' && products.length > 0 && (() => {
                const globalData = Array.from({ length: years }, (_, i) => {
                  const yr = i + 1;
                  let totalVol = 0, totalRev = 0, totalCost = 0, totalProfit = 0;
                  products.forEach(p => {
                    const proj = calculateProductProjection(p);
                    const y = proj[i];
                    if (y) {
                      totalVol += y.volume;
                      totalRev += y.revenue;
                      totalCost += y.totalCost;
                      totalProfit += y.margin;
                    }
                  });
                  return {
                    year: `Año ${yr}`,
                    ticketIngreso: totalVol > 0 ? totalRev / totalVol : 0,
                    ticketCosto: totalVol > 0 ? totalCost / totalVol : 0,
                    ticketUtilidad: totalVol > 0 ? totalProfit / totalVol : 0,
                    volumenTotal: totalVol,
                  };
                });
                return (
                  <Card className="border-primary/30" data-testid="ticket-promedio-global">
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-primary" />
                        Ticket Promedio Global
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">
                        Promedio ponderado por volumen de todos los productos
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <Table className="text-sm">
                        <TableHeader>
                          <TableRow className="bg-primary/10">
                            <TableHead className="font-bold text-primary">Métrica</TableHead>
                            {globalData.map((d, i) => (
                              <TableHead key={i} className="text-right font-bold text-primary">{d.year}</TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <TableRow>
                            <TableCell className="font-semibold text-muted-foreground">Volumen Total</TableCell>
                            {globalData.map((d, i) => (
                              <TableCell key={i} className="text-right tabular-nums">{formatVolumeValue(d.volumenTotal)}</TableCell>
                            ))}
                          </TableRow>
                          <TableRow className="bg-blue-500/5">
                            <TableCell className="font-semibold text-blue-400">Ticket Ingreso</TableCell>
                            {globalData.map((d, i) => (
                              <TableCell key={i} className="text-right tabular-nums font-semibold text-blue-400">{formatTableValue(d.ticketIngreso, currency)}</TableCell>
                            ))}
                          </TableRow>
                          <TableRow className="bg-red-500/5">
                            <TableCell className="font-semibold text-red-400">Ticket Costo</TableCell>
                            {globalData.map((d, i) => (
                              <TableCell key={i} className="text-right tabular-nums font-semibold text-red-400">{formatTableValue(d.ticketCosto, currency)}</TableCell>
                            ))}
                          </TableRow>
                          <TableRow className="bg-emerald-500/5 border-t-2 border-emerald-500/30">
                            <TableCell className="font-bold text-emerald-400">Ticket Utilidad</TableCell>
                            {globalData.map((d, i) => (
                              <TableCell key={i} className="text-right tabular-nums font-bold text-emerald-400">{formatTableValue(d.ticketUtilidad, currency)}</TableCell>
                            ))}
                          </TableRow>
                        </TableBody>
                      </Table>
                      <ResponsiveContainer width="100%" height={250}>
                        <ComposedChart data={globalData} margin={{ top: 20, right: 10, left: 10, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
                          <XAxis dataKey="year" stroke="#94A3B8" fontSize={12} />
                          <YAxis stroke="#94A3B8" fontSize={11} tickFormatter={(v) => formatChartValue(v)} domain={[0, (dataMax) => Math.ceil(dataMax * 1.15)]} />
                          <Tooltip contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142' }} formatter={(v) => formatTableValue(v, currency)} />
                          <Legend />
                          <Bar dataKey="ticketIngreso" fill="#3B82F6" name="Ticket Ingreso" radius={[4, 4, 0, 0]}>
                            {showLabels && <LabelList dataKey="ticketIngreso" position="top" formatter={(v) => formatChartValue(v)} fill="#3B82F6" fontSize={9} />}
                          </Bar>
                          <Bar dataKey="ticketCosto" fill="#EF4444" name="Ticket Costo" radius={[4, 4, 0, 0]}>
                            {showLabels && <LabelList dataKey="ticketCosto" position="top" formatter={(v) => formatChartValue(v)} fill="#EF4444" fontSize={9} />}
                          </Bar>
                          <Line type="monotone" dataKey="ticketUtilidad" stroke="#10B981" strokeWidth={2} name="Ticket Utilidad" dot={{ fill: '#10B981', r: 4 }}>
                            {showLabels && <LabelList dataKey="ticketUtilidad" position="top" formatter={(v) => formatChartValue(v)} fill="#10B981" fontSize={9} />}
                          </Line>
                        </ComposedChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* CAGR Summary Table */}
              {selectedProductFilter === 'all' && (
                <Card className="border-primary/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2">
                      <Info className="w-5 h-5 text-primary" />
                      Resumen CAGR por Producto
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Tasa de Crecimiento Anual Compuesta (Año 1 a Año {years})
                    </p>
                  </CardHeader>
                  <CardContent>
                    <Table className="text-sm">
                      <TableHeader>
                        <TableRow className="bg-primary/10">
                          <TableHead className="font-bold text-primary">Producto</TableHead>
                          <TableHead className="text-center font-bold text-primary">CAGR Volumen</TableHead>
                          <TableHead className="text-center font-bold text-primary">CAGR Ingresos</TableHead>
                          <TableHead className="text-center font-bold text-primary">CAGR Costos</TableHead>
                          <TableHead className="text-center font-bold text-primary">CAGR Utilidad</TableHead>
                          <TableHead className="text-center font-bold text-primary">Margen Año 1</TableHead>
                          <TableHead className="text-center font-bold text-primary">Margen Año {years}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {products.map((product, pIdx) => {
                          const proj = calculateProductProjection(product);
                          const n = years - 1;
                          const y1 = proj[0];
                          const yL = proj[proj.length - 1];
                          const volCAGR = calcCAGR(y1.volume, yL.volume, n);
                          const revCAGR = calcCAGR(y1.revenue, yL.revenue, n);
                          const costCAGR = calcCAGR(y1.totalCost, yL.totalCost, n);
                          const profitCAGR = calcCAGR(y1.margin, yL.margin, n);

                          return (
                            <TableRow key={product.id}>
                              <TableCell className="font-semibold">
                                <div className="flex items-center gap-2">
                                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[pIdx % COLORS.length] }} />
                                  {product.name}
                                </div>
                              </TableCell>
                              <TableCell className={cn("text-center font-bold", volCAGR > 0 ? "text-success" : volCAGR < 0 ? "text-destructive" : "text-muted-foreground")}>
                                {volCAGR > 0 ? '+' : ''}{volCAGR.toFixed(1)}%
                              </TableCell>
                              <TableCell className={cn("text-center font-bold", revCAGR > 0 ? "text-blue-400" : "text-destructive")}>
                                {revCAGR > 0 ? '+' : ''}{revCAGR.toFixed(1)}%
                              </TableCell>
                              <TableCell className={cn("text-center font-bold", costCAGR > 0 ? "text-red-400" : "text-success")}>
                                {costCAGR > 0 ? '+' : ''}{costCAGR.toFixed(1)}%
                              </TableCell>
                              <TableCell className={cn("text-center font-bold", profitCAGR > 0 ? "text-emerald-400" : "text-destructive")}>
                                {profitCAGR > 0 ? '+' : ''}{profitCAGR.toFixed(1)}%
                              </TableCell>
                              <TableCell className="text-center tabular-nums">{y1.marginPct.toFixed(1)}%</TableCell>
                              <TableCell className="text-center tabular-nums">{yL.marginPct.toFixed(1)}%</TableCell>
                            </TableRow>
                          );
                        })}
                        {/* Totals row */}
                        {(() => {
                          const totals = Array.from({ length: years }, (_, i) => {
                            return products.reduce((acc, p) => {
                              const proj = calculateProductProjection(p);
                              const yr = proj[i];
                              return {
                                volume: acc.volume + (yr?.volume || 0),
                                revenue: acc.revenue + (yr?.revenue || 0),
                                totalCost: acc.totalCost + (yr?.totalCost || 0),
                                margin: acc.margin + (yr?.margin || 0)
                              };
                            }, { volume: 0, revenue: 0, totalCost: 0, margin: 0 });
                          });
                          const t1 = totals[0]; const tL = totals[totals.length - 1];
                          const n = years - 1;
                          return (
                            <TableRow className="bg-primary/20 font-bold">
                              <TableCell className="text-primary">TOTAL</TableCell>
                              <TableCell className="text-center text-primary">{calcCAGR(t1.volume, tL.volume, n) > 0 ? '+' : ''}{calcCAGR(t1.volume, tL.volume, n).toFixed(1)}%</TableCell>
                              <TableCell className="text-center text-primary">{calcCAGR(t1.revenue, tL.revenue, n) > 0 ? '+' : ''}{calcCAGR(t1.revenue, tL.revenue, n).toFixed(1)}%</TableCell>
                              <TableCell className="text-center text-primary">{calcCAGR(t1.totalCost, tL.totalCost, n) > 0 ? '+' : ''}{calcCAGR(t1.totalCost, tL.totalCost, n).toFixed(1)}%</TableCell>
                              <TableCell className="text-center text-primary">{calcCAGR(t1.margin, tL.margin, n) > 0 ? '+' : ''}{calcCAGR(t1.margin, tL.margin, n).toFixed(1)}%</TableCell>
                              <TableCell className="text-center text-primary">{t1.revenue > 0 ? (t1.margin / t1.revenue * 100).toFixed(1) : 0}%</TableCell>
                              <TableCell className="text-center text-primary">{tL.revenue > 0 ? (tL.margin / tL.revenue * 100).toFixed(1) : 0}%</TableCell>
                            </TableRow>
                          );
                        })()}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Unit Economics */}
              <Card>
                <CardHeader>
                  <CardTitle>Economía Unitaria por Producto (Año 1)</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-secondary/30">
                        <TableHead>Producto</TableHead>
                        <TableHead className="text-right">Precio</TableHead>
                        <TableHead className="text-right">Costo</TableHead>
                        <TableHead className="text-right">Margen</TableHead>
                        <TableHead className="text-right">% Margen</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {products.map((product, idx) => {
                        const totalCost = getTotalCost(product);
                        const margin = product.unit_price - totalCost;
                        const marginPct = product.unit_price > 0 ? (margin / product.unit_price * 100) : 0;
                        return (
                          <TableRow key={product.id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                                {product.name}
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-medium">{formatTableValue(product.unit_price, currency)}</TableCell>
                            <TableCell className="text-right text-destructive">{formatTableValue(totalCost, currency)}</TableCell>
                            <TableCell className="text-right text-success font-medium">{formatTableValue(margin, currency)}</TableCell>
                            <TableCell className={cn("text-right font-bold", marginPct > 30 ? "text-success" : marginPct > 15 ? "text-warning" : "text-destructive")}>
                              {formatPercent(marginPct)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Product Edit Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Package className="w-6 h-6 text-primary" />
              {editingProduct?.name || 'Producto'}
            </DialogTitle>
          </DialogHeader>
          
          {editingProduct && (
            <div className="space-y-6 py-4">
              {/* Price Section */}
              <div className="p-4 bg-primary/10 rounded-lg border border-primary/30">
                <Label className="text-primary font-semibold">Precio de Venta Unitario (Año 1)</Label>
                <div className="flex items-center gap-2 mt-2">
                  <DollarSign className="w-5 h-5 text-muted-foreground" />
                  <Input
                    type="number" value={editingProduct.unit_price || ''} placeholder="0.00"
                    onChange={(e) => setEditingProduct(prev => ({ ...prev, unit_price: parseFloat(e.target.value) || 0 }))}
                    className="text-2xl font-bold"
                  />
                  <span className="text-muted-foreground">{currency}</span>
                </div>
              </div>

              {/* Price Growth Type */}
              <div>
                <Label className="mb-3 block font-semibold">Comportamiento del Precio en el Tiempo</Label>
                <div className="flex gap-2">
                  <Button type="button" size="sm" className="flex-1"
                    variant={editingProduct.price_growth_type === 'inflation' ? 'default' : 'outline'}
                    onClick={() => setEditingProduct(prev => ({ ...prev, price_growth_type: 'inflation' }))}>
                    <TrendingUp className="w-4 h-4 mr-2" />Inflación ({formatPercent(inflation)})
                  </Button>
                  <Button type="button" size="sm" className="flex-1"
                    variant={editingProduct.price_growth_type === 'manual' ? 'default' : 'outline'}
                    onClick={() => setEditingProduct(prev => ({ ...prev, price_growth_type: 'manual' }))}>
                    <Edit2 className="w-4 h-4 mr-2" />Override Manual
                  </Button>
                </div>
              </div>

              {/* Manual Price Override */}
              {editingProduct.price_growth_type === 'manual' && (
                <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                  <Label className="text-blue-400 font-semibold mb-2 block">Precio por Año (Override Manual)</Label>
                  <p className="text-xs text-muted-foreground mb-3">Año 1 usa el precio base. Define el precio para los años siguientes.</p>
                  <div className="grid grid-cols-5 gap-2">
                    {Array.from({ length: years }, (_, i) => {
                      const yr = i + 1;
                      const manualVal = editingProduct.manual_prices?.[String(yr)]?.price;
                      return (
                        <div key={yr}>
                          <p className="text-xs text-muted-foreground mb-1">Año {yr}</p>
                          <Input
                            type="number"
                            value={yr === 1 ? editingProduct.unit_price : (manualVal ?? '')}
                            disabled={yr === 1}
                            placeholder={String(editingProduct.unit_price || 0)}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value);
                              setEditingProduct(prev => ({
                                ...prev,
                                manual_prices: {
                                  ...prev.manual_prices,
                                  [String(yr)]: { ...(prev.manual_prices?.[String(yr)] || {}), price: val || 0 }
                                }
                              }));
                            }}
                            className="text-sm text-right"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Costs Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-lg font-semibold">Desglose de Costos (Año 1)</Label>
                  <Button size="sm" variant="outline"
                    onClick={() => setEditingProduct(prev => ({
                      ...prev, costs: [...(prev.costs || []), { id: crypto.randomUUID(), name: '', amount: 0, growth_type: 'inflation', manual_amounts: {} }]
                    }))}>
                    <Plus className="w-4 h-4 mr-1" />Agregar Costo
                  </Button>
                </div>
                <div className="space-y-3">
                  {(editingProduct.costs || []).map((cost, costIdx) => (
                    <div key={cost.id} className="p-3 bg-secondary/30 rounded-lg space-y-2">
                      {/* Cost name + amount row */}
                      <div className="flex items-center gap-3 group">
                        <div className="flex-1">
                          <Input value={cost.name} placeholder="Ej: Materia prima, Electricidad..."
                            onChange={(e) => {
                              const newCosts = [...editingProduct.costs];
                              newCosts[costIdx] = { ...cost, name: e.target.value };
                              setEditingProduct(prev => ({ ...prev, costs: newCosts }));
                            }}
                            className="bg-transparent border-0 focus-visible:ring-0 p-0 text-sm font-medium" />
                        </div>
                        <div className="flex items-center gap-2 w-36">
                          <span className="text-muted-foreground text-xs">Año 1: $</span>
                          <Input type="number" value={cost.amount || ''} placeholder="0"
                            onChange={(e) => {
                              const newCosts = [...editingProduct.costs];
                              newCosts[costIdx] = { ...cost, amount: parseFloat(e.target.value) || 0 };
                              setEditingProduct(prev => ({ ...prev, costs: newCosts }));
                            }}
                            className="text-right font-medium w-24" />
                        </div>
                        {editingProduct.costs.length > 1 && (
                          <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 h-8 w-8"
                            onClick={() => setEditingProduct(prev => ({ ...prev, costs: prev.costs.filter(c => c.id !== cost.id) }))}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                      {/* Per-cost growth type */}
                      <div className="flex items-center gap-2 pl-1">
                        <span className="text-xs text-muted-foreground">Crecimiento:</span>
                        <Button type="button" size="sm" variant={cost.growth_type !== 'manual' ? 'default' : 'outline'} className="h-6 text-xs px-2"
                          onClick={() => {
                            const newCosts = [...editingProduct.costs];
                            newCosts[costIdx] = { ...cost, growth_type: 'inflation' };
                            setEditingProduct(prev => ({ ...prev, costs: newCosts }));
                          }}>
                          Inflación ({formatPercent(inflation)})
                        </Button>
                        <Button type="button" size="sm" variant={cost.growth_type === 'manual' ? 'default' : 'outline'} className="h-6 text-xs px-2"
                          onClick={() => {
                            const newCosts = [...editingProduct.costs];
                            newCosts[costIdx] = { ...cost, growth_type: 'manual', manual_amounts: cost.manual_amounts || {} };
                            setEditingProduct(prev => ({ ...prev, costs: newCosts }));
                          }}>
                          <Edit2 className="w-3 h-3 mr-1" />Manual
                        </Button>
                      </div>
                      {/* Manual amounts per year */}
                      {cost.growth_type === 'manual' && (
                        <div className="grid grid-cols-5 gap-2 pt-1 pl-1">
                          {Array.from({ length: years }, (_, i) => {
                            const yr = i + 1;
                            const manualVal = cost.manual_amounts?.[String(yr)];
                            return (
                              <div key={yr}>
                                <p className="text-xs text-muted-foreground mb-1">Año {yr}</p>
                                <Input type="number"
                                  value={yr === 1 ? cost.amount : (manualVal ?? '')}
                                  disabled={yr === 1}
                                  placeholder={String(cost.amount || 0)}
                                  onChange={(e) => {
                                    const val = parseFloat(e.target.value);
                                    const newCosts = [...editingProduct.costs];
                                    newCosts[costIdx] = {
                                      ...cost,
                                      manual_amounts: { ...cost.manual_amounts, [String(yr)]: val || 0 }
                                    };
                                    setEditingProduct(prev => ({ ...prev, costs: newCosts }));
                                  }}
                                  className="text-xs text-right h-8" />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between p-4 bg-destructive/10 rounded-lg border border-destructive/30">
                  <span className="font-semibold text-destructive">Costo Total Unitario (Año 1)</span>
                  <span className="text-2xl font-bold text-destructive">
                    {formatCurrency(editingProduct.costs?.reduce((sum, c) => sum + (c.amount || 0), 0) || 0, currency)}
                  </span>
                </div>

                {editingProduct.unit_price > 0 && (
                  <div className="flex items-center justify-between p-4 bg-success/10 rounded-lg border border-success/30">
                    <span className="font-semibold text-success">Margen Unitario (Año 1)</span>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-success">
                        {formatCurrency(editingProduct.unit_price - (editingProduct.costs?.reduce((sum, c) => sum + (c.amount || 0), 0) || 0), currency)}
                      </span>
                      <span className="text-sm text-success ml-2">
                        ({formatPercent(((editingProduct.unit_price - (editingProduct.costs?.reduce((sum, c) => sum + (c.amount || 0), 0) || 0)) / editingProduct.unit_price * 100))})
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Projection Chart */}
              <div className="pt-4 border-t border-border">
                <Label className="mb-3 block font-semibold">Proyección de Precio y Costo ({years} años)</Label>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={(() => {
                      const proj = calculateProductProjection(editingProduct);
                      return proj.map(y => ({ year: `Año ${y.year}`, precio: y.price, costo: y.cost, margen: y.price - y.cost }));
                    })()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
                      <XAxis dataKey="year" stroke="#94A3B8" fontSize={10} />
                      <YAxis stroke="#94A3B8" fontSize={10} tickFormatter={(v) => formatChartValue(v)} domain={[0, 'auto']} />
                      <Tooltip contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142', fontSize: 12 }} formatter={(v) => formatCurrency(v, currency)} />
                      <Area type="monotone" dataKey="margen" fill="#10B98120" stroke="#10B981" name="Margen" />
                      <Line type="monotone" dataKey="precio" stroke="#3B82F6" strokeWidth={2} name="Precio" dot={{ fill: '#3B82F6', r: 3 }}>
                        {showLabels && <LabelList dataKey="precio" position="top" formatter={(v) => formatChartValue(v)} fill="#3B82F6" fontSize={9} />}
                      </Line>
                      <Line type="monotone" dataKey="costo" stroke="#EF4444" strokeWidth={2} name="Costo" dot={{ fill: '#EF4444', r: 3 }}>
                        {showLabels && <LabelList dataKey="costo" position="top" formatter={(v) => formatChartValue(v)} fill="#EF4444" fontSize={9} />}
                      </Line>
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProductDialog(false)}>Cancelar</Button>
            <Button onClick={saveProductFromDialog}><Save className="w-4 h-4 mr-2" />Guardar Cambios</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Actions */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={() => { if (!tryNavigate(`/app/project/${projectId}/sales-volume`)) navigate(`/app/project/${projectId}/sales-volume`); }}>
          <ChevronLeft className="w-4 h-4 mr-2" />Anterior
        </Button>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />{saving ? 'Guardando...' : 'Guardar'}
          </Button>
          <Button onClick={() => { if (!tryNavigate(`/app/project/${projectId}/fixed-expenses`)) navigate(`/app/project/${projectId}/fixed-expenses`); }} data-testid="continue-btn">
            Continuar<ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      <BlockerDialog showDialog={showDialog} onSave={handleSaveAndProceed} onDiscard={handleDiscard} onCancel={handleCancel} saving={saving} />
    </div>
  );
};
